import { z } from 'zod';
import { INQUIRY_STATUSES } from '../api.js';
import { SERVICE_CLASSES } from '../service-classes.js';
/**
 * Disposition im Chauffeur-Betrieb (KON-238): Aus einer Anfrage mit Preis entsteht direkt ein Auftrag, der Dispatcher weist ihn einem Fahrer mit Schicht an dem Tag und einem Fahrzeug zu, und der
 * Kunde bekommt per E-Mail erst die Bestätigung (Auftrag geplant) und dann die Angaben zu Fahrer und Fahrzeug.
 */
/** Felder einer Anfrage, die den vereinbarten Preis tragen (Schlüssel ohne Beachtung von Groß- und Kleinschreibung und Trennzeichen). Der Preis gilt als Bruttopreis in Euro. */
export const INQUIRY_PRICE_ALIASES = ['price', 'preis', 'agreedprice', 'fixedprice', 'festpreis', 'estimate', 'onlineschaetzung', 'amount', 'betrag'];
/** Steuersatz für den Preis aus der Anfrage. Ein Fahrpreis gilt als Bruttopreis, der Auftrag rechnet den Nettobetrag daraus. */
export const INQUIRY_PRICE_TAX_RATE = 19;
const norm = (key) => key.toLowerCase().replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss').replace(/[^a-z0-9]/g, '');
/** Bruttopreis in Cent aus den Feldern einer Anfrage, null ohne gültigen Preis. Zahlen und Texte wie „89,50“ oder „89.50 €“ sind erlaubt. */
export function inquiryPriceCents(fields) {
    if (!fields)
        return null;
    const byKey = new Map(Object.entries(fields).map(([k, v]) => [norm(k), v]));
    for (const alias of INQUIRY_PRICE_ALIASES) {
        const v = byKey.get(alias);
        let n = null;
        if (typeof v === 'number')
            n = v;
        else if (typeof v === 'string' && v.trim()) {
            const text = v.replace(/[^\d,.-]/g, '');
            // „1.234,50“ → 1234.50, „89,5“ → 89.5, „89.50“ bleibt
            const normalized = /,\d{1,2}$/.test(text) ? text.replace(/\./g, '').replace(',', '.') : text.replace(/,/g, '');
            const parsed = Number(normalized);
            n = Number.isFinite(parsed) ? parsed : null;
        }
        if (n !== null && n > 0 && n < 1_000_000)
            return Math.round(n * 100);
    }
    return null;
}
/** Abholdatum und -uhrzeit aus den Feldern einer Anfrage: ISO-Zeit, „21.10.2026 05:15“ oder Datum und Uhrzeit getrennt. Fehlt das Datum, gilt das Wunschdatum der Anfrage. */
export function inquiryPickup(fields, preferredDate) {
    const byKey = new Map(Object.entries(fields ?? {}).map(([k, v]) => [norm(k), typeof v === 'string' ? v : typeof v === 'number' ? String(v) : '']));
    const raw = ['pickuptime', 'pickupat', 'abholzeit', 'abholzeitpunkt', 'abfahrt', 'fahrtbeginn'].map((k) => byKey.get(k)).find((v) => v) ?? '';
    const timeOnly = ['uhrzeit', 'zeit', 'time', 'abholuhrzeit'].map((k) => byKey.get(k)).find((v) => v) ?? '';
    const iso = /(\d{4}-\d{2}-\d{2})/.exec(raw)?.[1];
    const de = /(\d{1,2})\.(\d{1,2})\.(\d{4})/.exec(raw);
    const date = iso ?? (de ? `${de[3]}-${de[2].padStart(2, '0')}-${de[1].padStart(2, '0')}` : null) ?? byKey.get('abholdatum') ?? preferredDate;
    const t = /(\d{1,2}):(\d{2})/.exec(raw) ?? /(\d{1,2}):(\d{2})/.exec(timeOnly);
    return { date: date && /^\d{4}-\d{2}-\d{2}$/.test(date) ? date : null, time: t ? `${t[1].padStart(2, '0')}:${t[2]}` : null };
}
/** Rechnungsanschrift aus den Feldern einer Anfrage („Billing street“, „Billing zip“, „Billing city“ oder deutsche Bezeichnungen), null ohne Angaben */
export function inquiryBillingAddress(fields) {
    const byKey = new Map(Object.entries(fields ?? {}).map(([k, v]) => [norm(k), typeof v === 'string' ? v.trim() : typeof v === 'number' ? String(v) : '']));
    const pick = (...keys) => keys.map((k) => byKey.get(k)).find((v) => v) || null;
    const street = pick('billingstreet', 'billingaddress', 'rechnungsstrasse', 'rechnungsadresse', 'rechnungsanschrift');
    const postalCode = pick('billingzip', 'billingpostalcode', 'billingpostcode', 'rechnungsplz', 'rechnungspostleitzahl');
    const city = pick('billingcity', 'billingtown', 'rechnungsort', 'rechnungsstadt');
    return street || postalCode || city ? { street: street?.slice(0, 200) ?? null, postalCode: postalCode?.slice(0, 20) ?? null, city: city?.slice(0, 100) ?? null } : null;
}
export const orderFromInquiryInput = z
    .object({
    /** Bruttopreis in Cent, wenn die Anfrage keinen enthält. Ein Preis aus der Anfrage hat Vorrang, ein Preis hier überschreibt ihn nicht. */
    priceGrossCents: z.number().int().min(1, 'Der Preis muss größer als 0 sein').max(100_000_000).optional(),
    /** Preis aus der Anfrage ausdrücklich ersetzen (der Dispatcher hat ihn im Fenster geändert) */
    overridePrice: z.boolean().default(false),
})
    .strict();
export const dispatchInput = z
    .object({
    employeeId: z.uuid('Bitte einen Fahrer wählen'),
    vehicleId: z.uuid('Bitte ein Fahrzeug wählen'),
    startsAt: z.string().trim().regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/, 'Bitte Datum und Uhrzeit wie 2026-10-15T08:00 angeben'),
    endsAt: z.string().trim().regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/, 'Bitte Datum und Uhrzeit wie 2026-10-15T08:00 angeben'),
    /** Den Kunden über Fahrer und Fahrzeug informieren (Vorgabe: ja) */
    notifyCustomer: z.boolean().default(true),
    /** Der Benutzer hat die Warnungen gesehen (Ruhezeit, Überschneidung, falsche Klasse …) und bestätigt die Zuweisung trotzdem */
    confirmWarnings: z.boolean().default(false),
})
    .strict()
    .refine((v) => v.endsAt > v.startsAt, { message: 'Das Ende muss nach dem Beginn liegen', path: ['endsAt'] });
/** Ein Fahrer wählt für seine angenommene Fahrt selbst ein Fahrzeug */
export const pickVehicleInput = z.object({ vehicleId: z.uuid('Bitte ein Fahrzeug wählen'), confirmWarnings: z.boolean().default(false) }).strict();
/** Fahrt annehmen, optional mit bestätigten Warnungen */
export const claimInput = z.object({ confirmWarnings: z.boolean().default(false) }).strict();
/** Fehlercode, mit dem der Server Warnungen meldet, die der Benutzer bestätigen kann. Die Texte stehen in den `fields` (`w1`, `w2` …). */
export const CONFIRM_REQUIRED = 'confirm_required';
/** Klasse des Auftrags ändern (Dispatcher), leer hebt die Anforderung auf */
export const setServiceClassInput = z.object({ serviceClass: z.enum(SERVICE_CLASSES, 'Bitte die Klasse wählen').nullable() }).strict();
export { INQUIRY_STATUSES };
//# sourceMappingURL=dispatch.js.map